import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import HeroSection from '../../sections/HeroSection/HeroSection';
import styles from './Reservations.module.css';

const OCCASIONS = [
    'Casual Dining', 'Birthday Celebration', 'Anniversary',
    'Corporate Dinner', 'Private Event', 'Date Night',
    'Family Gathering', 'Other',
];

const GUEST_OPTIONS = [
    { value: '1', label: '1 Guest' },
    { value: '2', label: '2 Guests' },
    { value: '3', label: '3 Guests' },
    { value: '4', label: '4 Guests' },
    { value: '5', label: '5 Guests' },
    { value: '6', label: '6+ Guests' },
];

const TIME_SLOTS = [
    '6:30 PM – 9:00 PM',
    '9:00 PM – 11:30 PM',
];

const INITIAL = {
    name: '', phone: '', email: '',
    date: '', time: '',
    guests: '', occasion: '',
    notes: '',
};

const todayStr = () => new Date().toISOString().split('T')[0];

const ChevronIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const StepDot = ({ step, current, label }) => (
    <div className={styles.stepItem}>
        <div className={`${styles.stepDot} ${current === step ? styles.stepDotActive : ''} ${current > step ? styles.stepDotDone : ''}`}>
            {current > step ? '✓' : step}
        </div>
        <span className={`${styles.stepLabel} ${current === step ? styles.stepLabelActive : ''}`}>{label}</span>
    </div>
);

const SelectField = ({ id, name, value, onChange, children, error }) => (
    <div className={styles.selectWrap}>
        <select
            id={id} name={name}
            className={`${styles.input} ${error ? styles.inputError : ''}`}
            value={value}
            onChange={onChange}
        >
            {children}
        </select>
        <span className={styles.selectArrow}><ChevronIcon /></span>
    </div>
);

const Reservations = () => {
    const [form, setForm] = useState(INITIAL);
    const [step, setStep] = useState(1);
    const [touched, setTouched] = useState({});
    const [status, setStatus] = useState('idle');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const step1Valid = form.name.trim() && form.phone.trim() && form.email.includes('@');
    const step2Valid = form.date && form.time && form.guests;

    const touchStep1 = () => setTouched({ name: true, phone: true, email: true });
    const touchStep2 = () => setTouched(p => ({ ...p, date: true, time: true, guests: true }));

    const goNext = () => {
        if (step === 1 && !step1Valid) { touchStep1(); return; }
        if (step === 2 && !step2Valid) { touchStep2(); return; }
        setStep(s => Math.min(s + 1, 3));
    };

    const goPrev = () => setStep(s => Math.max(s - 1, 1));

    const handleConfirm = async () => {
        if (step !== 3) return;
        setStatus('sending');
        try {
            // 1. Send the email and sync with Rista CRM via your VPS backend
            const response = await fetch('/api/reservation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });

            if (!response.ok) {
                throw new Error('Backend processing failed');
            }

            // 2. If the email/API was successful, store the details in Firebase
            await addDoc(collection(db, 'reservations'), {
                ...form,
                submittedAt: serverTimestamp(),
            });

            // 3. Best-effort: log to Google Sheets (failure won't block success)
            if (process.env.REACT_APP_SHEETS_WEBHOOK) {
                fetch(process.env.REACT_APP_SHEETS_WEBHOOK, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ type: 'reservation', ...form }),
                }).catch(err => console.error('Sheets webhook failed:', err));
            }

            setStatus('success');
        } catch (err) {
            console.error('Submission error:', err);
            setStatus('error');
        }
    };

    const err = (field) => touched[field] && !form[field]?.trim();
    const emailErr = touched.email && !form.email.includes('@');

    return (
        <>
            <HeroSection
                title="Reserve a Table"
                image="/images/33-DSC09445.jpg"
                imageAlt="Berli Brewing Co."
            />

            <section className={styles.section}>
                <div className={styles.container}>

                    {/* ── Left — Brand panel ── */}
                    <aside className={styles.brandPanel}>
                        <div className={styles.brandInner}>
                            <span className={styles.brandEyebrow}>Berli Brewing Co.</span>
                            <h2 className={styles.brandHeading}>
                                Come as you are.<br />
                                <em>Leave feeling at home.</em>
                            </h2>
                            <p className={styles.brandBody}>
                                We seat a limited number of guests each evening to make sure every visit feels personal. Book your spot and let us take care of the rest — we'll be ready at the door.
                            </p>

                            <div className={styles.brandDetails}>
                                <div className={styles.brandDetailRow}>
                                    <span className={styles.brandDetailIcon}>◷</span>
                                    <div>
                                        <span className={styles.brandDetailLabel}>Hours</span>
                                        <span className={styles.brandDetailValue}>
                                            Tue – Sun · 1:00 PM – 1:30 AM <br /> 
                                            Mon · 6:00 PM – 1:30 AM (Dinner only)
                                        </span>                                    
                                    </div>
                                </div>
                                <div className={styles.brandDetailRow}>
                                    <span className={styles.brandDetailIcon}>✦</span>
                                    <div>
                                        <span className={styles.brandDetailLabel}>Walk-ins</span>
                                        <span className={styles.brandDetailValue}>Always welcome, subject to availability</span>
                                    </div>
                                </div>
                                <div className={styles.brandDetailRow}>
                                    <span className={styles.brandDetailIcon}>☎</span>
                                    <div>
                                        <span className={styles.brandDetailLabel}>Phone</span>
                                        <span className={styles.brandDetailValue}>+91 74200 01640</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.brandRule} />
                            <p className={styles.brandNote}>
                                For groups larger than 6 or private events, please reach out directly at{' '}
                                <a href="mailto:contact@berlibrewing.com" className={styles.brandEmail}>
                                    contact@berlibrewing.com
                                </a>
                            </p>
                        </div>
                        <div className={styles.brandGrain} aria-hidden="true" />
                    </aside>

                    {/* ── Right — Multi-step form ── */}
                    <div className={styles.formCard}>

                        {status === 'success' ? (
                            <div className={styles.successState}>
                                <div className={styles.successMark}>✦</div>
                                <h3 className={styles.successTitle}>You're on the list.</h3>
                                <p className={styles.successBody}>
                                    Thank you, <strong>{form.name}</strong>. We've received your reservation
                                    for <strong>{form.date}</strong> at <strong>{form.time}</strong> for{' '}
                                    <strong>{form.guests} {form.guests === '1' ? 'guest' : 'guests'}</strong>.
                                    We'll confirm via phone or email shortly — see you soon.
                                </p>
                                <button
                                    type="button"
                                    className={styles.newResBtn}
                                    onClick={() => { setForm(INITIAL); setStep(1); setStatus('idle'); setTouched({}); }}
                                >
                                    Make Another Reservation
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Step indicator */}
                                <div className={styles.stepBar}>
                                    <StepDot step={1} current={step} label="Your Details" />
                                    <div className={`${styles.stepLine} ${step > 1 ? styles.stepLineDone : ''}`} />
                                    <StepDot step={2} current={step} label="Date & Time" />
                                    <div className={`${styles.stepLine} ${step > 2 ? styles.stepLineDone : ''}`} />
                                    <StepDot step={3} current={step} label="Occasion" />
                                </div>

                                <div className={styles.form}>

                                    {/* Step 1 — Personal details */}
                                    {step === 1 && (
                                        <div className={styles.stepPane}>
                                            <h3 className={styles.stepTitle}>Who's joining us?</h3>

                                            <div className={styles.fieldGroup}>
                                                <label className={styles.fieldLabel} htmlFor="name">
                                                    Full Name <span className={styles.req}>*</span>
                                                </label>
                                                <input id="name" name="name" type="text"
                                                    className={`${styles.input} ${err('name') ? styles.inputError : ''}`}
                                                    placeholder="Your full name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                                />
                                                {err('name') && <span className={styles.errMsg}>Name is required</span>}
                                            </div>

                                            <div className={styles.formRow}>
                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.fieldLabel} htmlFor="phone">
                                                        Phone <span className={styles.req}>*</span>
                                                    </label>
                                                    <input id="phone" name="phone" type="tel"
                                                        className={`${styles.input} ${err('phone') ? styles.inputError : ''}`}
                                                        placeholder="+91 00000 00000"
                                                        value={form.phone}
                                                        onChange={handleChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                                    />
                                                    {err('phone') && <span className={styles.errMsg}>Phone is required</span>}
                                                </div>

                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.fieldLabel} htmlFor="email">
                                                        Email <span className={styles.req}>*</span>
                                                    </label>
                                                    <input id="email" name="email" type="email"
                                                        className={`${styles.input} ${emailErr ? styles.inputError : ''}`}
                                                        placeholder="you@example.com"
                                                        value={form.email}
                                                        onChange={handleChange}
                                                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                                    />
                                                    {emailErr && <span className={styles.errMsg}>Valid email required</span>}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2 — Date, time, guests */}
                                    {step === 2 && (
                                        <div className={styles.stepPane}>
                                            <h3 className={styles.stepTitle}>When are you coming in?</h3>

                                            <div className={styles.formRow}>
                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.fieldLabel} htmlFor="date">
                                                        Date <span className={styles.req}>*</span>
                                                    </label>
                                                    <input id="date" name="date" type="date"
                                                        min={todayStr()}
                                                        className={`${styles.input} ${styles.dateInput} ${touched.date && !form.date ? styles.inputError : ''}`}
                                                        value={form.date}
                                                        onChange={handleChange}
                                                    />
                                                    {touched.date && !form.date && <span className={styles.errMsg}>Date is required</span>}
                                                </div>

                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.fieldLabel} htmlFor="time">
                                                        Slot <span className={styles.req}>*</span>
                                                    </label>
                                                    <SelectField id="time" name="time" value={form.time} onChange={handleChange}
                                                        error={touched.time && !form.time}>
                                                        <option value="">Select a slot</option>
                                                        {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </SelectField>
                                                    {touched.time && !form.time && <span className={styles.errMsg}>Please select a slot</span>}
                                                </div>
                                            </div>

                                            <div className={styles.fieldGroup}>
                                                <label className={styles.fieldLabel}>
                                                    Number of Guests <span className={styles.req}>*</span>
                                                </label>
                                                <div className={styles.guestPicker}>
                                                    {GUEST_OPTIONS.map(({ value, label }) => (
                                                        <button
                                                            key={value}
                                                            type="button"
                                                            className={`${styles.guestBtn} ${form.guests === value ? styles.guestBtnActive : ''}`}
                                                            onClick={() => {
                                                                setForm(p => ({ ...p, guests: value }));
                                                                setTouched(p => ({ ...p, guests: true }));
                                                            }}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {touched.guests && !form.guests && (
                                                    <span className={styles.errMsg}>Please select number of guests</span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3 — Occasion + notes + summary */}
                                    {step === 3 && (
                                        <div className={styles.stepPane}>
                                            <h3 className={styles.stepTitle}>What's the occasion?</h3>

                                            <div className={styles.fieldGroup}>
                                                <label className={styles.fieldLabel}>Occasion</label>
                                                <div className={styles.occasionGrid}>
                                                    {OCCASIONS.map(o => (
                                                        <button
                                                            key={o}
                                                            type="button"
                                                            className={`${styles.occasionBtn} ${form.occasion === o ? styles.occasionBtnActive : ''}`}
                                                            onClick={() => setForm(p => ({ ...p, occasion: o }))}
                                                        >
                                                            {o}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className={styles.fieldGroup}>
                                                <label className={styles.fieldLabel} htmlFor="notes">Special Requests</label>
                                                <textarea id="notes" name="notes" rows={4}
                                                    className={`${styles.input} ${styles.textarea}`}
                                                    placeholder="Dietary requirements, seating preferences, bringing a dog along..."
                                                    value={form.notes}
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className={styles.summaryCard}>
                                                <span className={styles.summaryLabel}>Reservation Summary</span>
                                                <div className={styles.summaryGrid}>
                                                    <span className={styles.summaryKey}>Name</span>
                                                    <span className={styles.summaryVal}>{form.name}</span>
                                                    <span className={styles.summaryKey}>Date</span>
                                                    <span className={styles.summaryVal}>{form.date}</span>
                                                    <span className={styles.summaryKey}>Slot</span>
                                                    <span className={styles.summaryVal}>{form.time}</span>
                                                    <span className={styles.summaryKey}>Guests</span>
                                                    <span className={styles.summaryVal}>{form.guests}</span>
                                                    {form.occasion && (
                                                        <>
                                                            <span className={styles.summaryKey}>Occasion</span>
                                                            <span className={styles.summaryVal}>{form.occasion}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Nav buttons */}
                                    <div className={styles.navRow}>
                                        {step > 1 && (
                                            <button type="button" className={styles.backBtn} onClick={goPrev}>
                                                ← Back
                                            </button>
                                        )}

                                        {step < 3 ? (
                                            <button type="button" className={styles.nextBtn} onClick={goNext}>
                                                Continue →
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.nextBtn}
                                                disabled={status === 'sending'}
                                                onClick={handleConfirm}
                                            >
                                                {status === 'sending' ? <span className={styles.spinner} /> : 'Confirm Reservation'}
                                            </button>
                                        )}
                                    </div>

                                    {status === 'error' && (
                                        <p className={styles.submitError}>Something went wrong. Please try again.</p>
                                    )}

                                </div>
                            </>
                        )}
                    </div>

                </div>
            </section>
        </>
    );
};

export default Reservations;